import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Seo } from '../components/Seo'
import { Dropzone } from '../components/Dropzone'
import { useAuth } from '../auth/AuthProvider'

interface ProjectMeta {
  id: string
  name: string | null
  entry_path: string
  total_bytes: number
  file_count: number
  created_at: number
}

function basename(path: string): string {
  return path.split('/').pop() || path
}

type ProjectState =
  | { status: 'loading' }
  | { status: 'ok'; project: ProjectMeta }
  | { status: 'notfound' }

/**
 * /update/:id — 覆盖更新已有项目：整批上传新文件后 PUT 全量替换，
 * 预览链接不变；name 留空则保留原名。
 */
export function Update() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const { user, loading, login } = useAuth()
  const [state, setState] = useState<ProjectState>({ status: 'loading' })

  useEffect(() => {
    if (!user) return
    let disposed = false
    setState({ status: 'loading' })
    void (async () => {
      try {
        const res = await fetch(`/api/projects/${id}`, { credentials: 'same-origin' })
        if (disposed) return
        if (!res.ok) {
          setState({ status: 'notfound' })
          return
        }
        const data = (await res.json()) as { project: ProjectMeta }
        if (!disposed) setState({ status: 'ok', project: data.project })
      } catch {
        if (!disposed) setState({ status: 'notfound' })
      }
    })()
    return () => {
      disposed = true
    }
  }, [user, id])

  const title = state.status === 'ok' ? state.project.name || basename(state.project.entry_path) : ''

  return (
    <main className="page container">
      <Seo title={t('seo.updateTitle')} description={t('seo.homeDesc')} path={`/update/${id}`} noIndex />

      <Link className="btn ghost sm back-link" to="/records">
        ← {t('updatePage.back')}
      </Link>
      <h1 className="page-title">
        {state.status === 'ok' ? t('updatePage.titleNamed', { name: title }) : t('updatePage.title')}
      </h1>

      {loading || !user ? (
        loading ? (
          <div className="spinner" aria-label="loading" />
        ) : (
          <div className="card login-card">
            <div className="icon" aria-hidden="true">🔐</div>
            <h3>{t('auth.loginRequiredTitle')}</h3>
            <p>{t('auth.loginRequiredDesc')}</p>
            <button type="button" className="btn primary" onClick={login}>
              {t('auth.login')}
            </button>
          </div>
        )
      ) : state.status === 'loading' ? (
        <div className="spinner" aria-label="loading" />
      ) : state.status === 'notfound' ? (
        <div className="card empty-state">
          <div className="icon" aria-hidden="true">🧭</div>
          <h3>{t('updatePage.notFound')}</h3>
          <Link className="btn primary" to="/records">
            {t('updatePage.back')}
          </Link>
        </div>
      ) : (
        <Dropzone projectId={state.project.id} projectTitle={title} />
      )}
    </main>
  )
}
